import { PropsWithChildren, useEffect } from 'react'

import { useGetMe } from '@/api/react-query'
import { I18nProvider as BaseI18nProvider, useI18n } from '@/i18n/i18n-react'
import type { Locales } from '@/i18n/i18n-types'
import { isLocale } from '@/i18n/i18n-util'
import { StoredStringSubject } from '@/utils/storage'

export const detectBrowserLocale = (): Locales => {
	if (typeof navigator === 'undefined') return 'en'
	const candidates = [navigator.language, ...(navigator.languages ?? [])]
	for (const candidate of candidates) {
		const code = candidate.split('-')[0]
		if (isLocale(code)) return code
	}
	return 'en'
}

// `I18nProvider` accepts `locale$: StackBehaviourSubject<Locales | undefined>` (it detects/sanitises an
// undefined starting locale), so the subject's generic must include `undefined` to stay assignable.
export const localeSubject = new StoredStringSubject<Locales | undefined>('locale', detectBrowserLocale())

export const I18nStorageSync = ({ children }: PropsWithChildren) => {
	const { locale, setLocale } = useI18n()
	const me = useGetMe()

	useEffect(() => {
		if (me.data?.locale && isLocale(me.data.locale) && locale !== me.data.locale) {
			setLocale(me.data.locale)
		}
	}, [me.data?.locale, locale, setLocale])

	return <>{children}</>
}

export const AppI18nProvider = ({ children }: PropsWithChildren) => {
	return (
		<BaseI18nProvider locale$={localeSubject} defaultLocales={[detectBrowserLocale()]}>
			<I18nStorageSync>{children}</I18nStorageSync>
		</BaseI18nProvider>
	)
}
